import { DialogHeading } from '@ariakit/react';
import { useEffect, useState } from 'react';
import type { FetcherWithComponents } from 'react-router';
import { useFetcher } from 'react-router';
import type { PostAuthOptions } from 'types';
import type { Image as ImageType } from 'types/capi/shared';

import ChevronIcon from '~/assets/icons/Chevron';
import TickIcon from '~/assets/icons/Tick';
import VectorIcon from '~/assets/icons/Vector';
import { Dialog, DialogDismiss } from '~/components/Dialog';
import Image from '~/components/Image';
import type { NotificationData } from '~/components/ProductNotificationButton';
import gtm from '~/services/gtm.client';
import { storeNotificationData } from '~/utils/browser';

import InitialScreen from '../InitialScreen';
import EmailLogin from '../LoginFlow';
import CancelNotifications from './CancelNotifications';
import ConfirmEmailReminder from './ConfirmEmailReminder';
import EmailEntry from './EmailEntry';
import EmailSubmitted from './EmailSubmitted';
import EmailSubmitting from './EmailSubmitting';

enum Stage {
  Error = -1,
  Initial = 0,
  EmailInput = 1,
  EmailSubmitting = 2,
  EmailRegistered = 3,
  Login = 4,
  Cancel = 5,
  ExitReminder = 6
}

const INTERCEPTED_STAGES = [Stage.Initial, Stage.Login, Stage.EmailInput];

export type FetcherData = {
  email?: string;
  errors?: {
    conflict?: boolean;
  };
};

function getStage(fetcher: FetcherWithComponents<FetcherData>) {
  const fetcherData = fetcher.data;

  function getIdleStage() {
    if (!fetcher.data) {
      return Stage.EmailInput;
    }
    if (!fetcherData?.errors) {
      return Stage.EmailRegistered;
    }

    return Stage.EmailInput;
  }

  switch (fetcher.state) {
    case 'idle':
      return getIdleStage();
    case 'submitting':
    case 'loading':
      return Stage.EmailSubmitting;
  }
}

type Props = {
  onDismiss: (options?: { loginSuccessful?: boolean }) => void;
  postAuthOptions: PostAuthOptions;
  product: NotificationData;
};

export default function EmailSignUpForNotification({
  onDismiss,
  postAuthOptions,
  product
}: Props) {
  const [stage, setStage] = useState(Stage.Initial);
  const [previousStage, setPreviousStage] = useState<Stage>(Stage.Initial);

  const fetcher = useFetcher<FetcherData>();

  useEffect(() => {
    if (stage === Stage.Initial || stage >= Stage.Login) return;

    const newStage = getStage(fetcher) ?? Stage.Initial;
    setStage(newStage);

    if (
      newStage === Stage.EmailRegistered &&
      postAuthOptions.action === 'restock'
    ) {
      storeNotificationData(postAuthOptions);
    }
  }, [fetcher, fetcher.state, stage, postAuthOptions]);

  function handleDismiss(options?: { loginSuccessful?: boolean }) {
    if (options?.loginSuccessful) {
      onDismiss(options);

      return;
    }

    if (stage === Stage.EmailRegistered) {
      setStage(Stage.ExitReminder);

      return;
    }

    if (INTERCEPTED_STAGES.includes(stage)) {
      gtm.trackedButtonClick({
        buttonName: 'X',
        pagePosition: 'Sign in modal'
      });
      setPreviousStage(stage);
      setStage(Stage.Cancel);

      return;
    }

    onDismiss();
  }

  function onBackNavigation() {
    setStage(stage - 1);
  }

  if (stage === Stage.Login) {
    return (
      <EmailLogin
        onBackNavigation={() => setStage(Stage.Initial)}
        modalType="notifications"
        onDismiss={handleDismiss}
        postAuthOptions={postAuthOptions}
      />
    );
  }

  if (stage === Stage.Cancel) {
    return (
      <CancelNotifications
        image={product.image}
        onGoBack={() => setStage(previousStage)}
        onCancel={onDismiss}
      />
    );
  }

  if (stage === Stage.ExitReminder) {
    return (
      <ConfirmEmailReminder
        email={fetcher.data?.email ?? ''}
        image={product.image}
        onDismiss={onDismiss}
      />
    );
  }

  return (
    <Dialog
      isOpen={true}
      onDismiss={handleDismiss}
      className={`z-50 rounded-lg bg-white p-4 text-black max-md:mx-4 md:w-[450px]`}
    >
      {stage === Stage.Initial ? (
        <InitialScreen
          onDismiss={handleDismiss}
          onSignUpClick={() => setStage(Stage.EmailInput)}
          postAuthOptions={postAuthOptions}
          onLogInClick={() => {
            setStage(Stage.Login);
          }}
          image={product.image}
          modalType="notifications"
        />
      ) : (
        <RegisterScreen
          onBackNavigation={
            stage < Stage.EmailSubmitting ? onBackNavigation : undefined
          }
          onDismiss={handleDismiss}
          fetcher={fetcher}
          product={product}
          stage={stage}
        />
      )}
    </Dialog>
  );
}

type RegisterScreenProps = {
  fetcher: FetcherWithComponents<FetcherData>;
  onBackNavigation?: () => void;
  onDismiss: Props['onDismiss'];
  product: Props['product'];
  stage: Stage;
};

function RegisterScreen({
  onBackNavigation,
  onDismiss,
  stage,
  fetcher,
  product
}: RegisterScreenProps) {
  return (
    <div className="flex w-auto max-w-[500px] flex-col items-center justify-center gap-4">
      <nav className="flex w-full flex-row items-center justify-between">
        <span className="flex min-w-6 shrink items-center justify-center self-stretch">
          {onBackNavigation && (
            <button className="w-full rotate-90" onClick={onBackNavigation}>
              <ChevronIcon stroke="black" width="14" height="14" />
            </button>
          )}
        </span>

        <div className="flex w-full flex-col items-center gap-4 text-center">
          <DialogHeading className="flex flex-col content-center self-stretch text-lg font-semibold">
            Confirm notifications
          </DialogHeading>
        </div>

        <DialogDismiss className="flex min-w-6 shrink items-center justify-center self-stretch text-black">
          <VectorIcon fill="black" width="10" height="10" />
        </DialogDismiss>
      </nav>
      {product.image && <ProductImage image={product.image} />}
      <Progress stage={stage} />
      <MainBody
        stage={stage}
        fetcher={fetcher}
        product={product}
        onDismiss={onDismiss}
      />
    </div>
  );
}

type MainBodyProps = {
  fetcher: FetcherWithComponents<FetcherData>;
  onDismiss: (options?: { loginSuccessful?: boolean }) => void;
  product: NotificationData;
  stage: Stage;
};

function MainBody({ fetcher, product, onDismiss, stage }: MainBodyProps) {
  const fetcherData = fetcher.data as {
    email?: string;
    errors?: { conflict: boolean };
  };

  switch (stage) {
    case Stage.EmailInput:
      return <EmailEntry product={product} fetcher={fetcher} />;
    case Stage.EmailSubmitting:
      return <EmailSubmitting />;
    case Stage.EmailRegistered:
      return <EmailSubmitted email={fetcherData.email!} />;

    case Stage.Error:
      onDismiss({ loginSuccessful: false });
  }
}

function ProductImage({ image }: { image: ImageType }) {
  return (
    <div className="flex items-center justify-center">
      <Image className="h-12" src={image.url} alt={image.alt} loading="lazy" />
    </div>
  );
}

function Progress({ stage }: { stage: Stage }) {
  return (
    <div className="relative z-10 rounded-full bg-grey-panel p-1 text-xs">
      <span
        className={`absolute inset-0 -z-10 rounded-full bg-purple-hola ${stage > Stage.EmailInput ? 'w-2/3' : 'w-[30%]'}`}
      ></span>
      <div className="mx-auto flex flex-row items-center justify-center gap-4 text-white">
        <span className="flex w-[50px] items-center justify-center gap-1">
          <span>Size</span> <TickIcon width="12" height="12" fill="white" />
        </span>

        <span
          className={`flex w-[50px] items-center justify-center gap-1 ${stage > Stage.EmailInput ? 'text-white' : 'text-purple-hola'}`}
        >
          <span>Email</span>
          {stage > Stage.EmailInput && (
            <TickIcon width="12" height="12" fill="white" />
          )}
        </span>

        <span
          className={`flex w-[50px] ${stage >= Stage.EmailSubmitting ? 'text-purple-hola' : 'text-grey-divide'}`}
        >
          Confirm
        </span>
      </div>
    </div>
  );
}
